import { Participant, LeaderboardEntry } from '../types';

/**
 * Leaderboard calculation utilities
 * Calculates leaderboard entries from participants
 */

/**
 * Calculate leaderboard from participants
 * Sorted by winningsBalance (descending), only winnings count
 * 
 * @param participants - Array of participants
 * @returns Sorted array of leaderboard entries
 */
export function calculateLeaderboard(participants: Participant[]): LeaderboardEntry[] {
  const entries: LeaderboardEntry[] = participants.map((participant) => {
    const bets = participant.bets;
    const totalBets = bets.length;
    const wins = bets.filter((b) => b.status === 'won').length;
    const losses = bets.filter((b) => b.status === 'lost').length;
    const winPercentage = totalBets > 0 ? (wins / totalBets) * 100 : 0;

    // Calculate additional stats
    const totalAmountBet = bets.reduce((sum, bet) => {
      // For parlay bets, use the full amount (not split across legs)
      return sum + bet.amount;
    }, 0);

    const lostBets = bets.filter((b) => b.status === 'lost');
    const amountLost = lostBets.reduce((sum, bet) => sum + bet.amount, 0);

    return {
      participantId: participant.id,
      participantName: participant.name,
      winningsBalance: participant.winningsBalance, // Primary metric
      totalBets,
      wins,
      losses,
      winPercentage,
      rank: 0, // Will be set after sorting
      // Extended stats
      totalAmountBet,
      initialStakeRemaining: participant.initialStakeRemaining,
      amountLost,
      totalAvailable: participant.totalAvailable,
    };
  });

  // Sort by winningsBalance (descending)
  entries.sort((a, b) => b.winningsBalance - a.winningsBalance);

  // Assign ranks
  entries.forEach((entry, index) => {
    entry.rank = index + 1;
  });

  return entries;
}

/**
 * Get leaderboard entry for a specific participant
 * 
 * @param participants - Array of participants
 * @param participantId - Participant ID to find
 * @returns Leaderboard entry or undefined
 */
export function getLeaderboardEntry(
  participants: Participant[],
  participantId: string
): LeaderboardEntry | undefined {
  const leaderboard = calculateLeaderboard(participants);
  return leaderboard.find((entry) => entry.participantId === participantId);
}

/**
 * Get top N participants on leaderboard
 * 
 * @param participants - Array of participants
 * @param topN - Number of top participants to return (default: 5)
 * @returns Top N leaderboard entries
 */
export function getTopParticipants(
  participants: Participant[],
  topN: number = 5
): LeaderboardEntry[] {
  const leaderboard = calculateLeaderboard(participants);
  return leaderboard.slice(0, topN);
}




